// src/pages/Community/LocationEditModal.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  useId,
} from "react";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { doc, onSnapshot, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import { toast } from "react-toastify";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Modal from "../../Community/components/Modal";
import { GTA_GAME_TABS as GAMES } from "../components/ToolbarHeader";

const DEFAULT_GAME_MAP_IMG = "/assets/gta6-map.jpg";
const MAX_IMAGE_BYTES = 6 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const labelByKey = (k) => GAMES.find((g) => g.key === k)?.label || "Other";

export default function LocationEditModal({
  open,
  onClose,
  locationId,
  user,
  role,
}) {
  const isAuthed = Boolean(user?.uid);
  const isAdmin = ["admin", "superadmin"].includes(
    String(user?.role || role || "").toLowerCase()
  );

  const storage = getStorage();
  const titleInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [docData, setDocData] = useState(null);
  const [previewDesc, setPreviewDesc] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [issues, setIssues] = useState([]);

  const [form, setForm] = useState({
    gameKey: "gta-6",
    game: labelByKey("gta-6"),
    title: "",
    category: "Hidden Location",
    city: "",
    region: "",
    description: "",
    tagsCsv: "",
    coverFile: null,
    coverUrl: "",
    images: [""],
    shortNote: "",
    mapImage: "",
    xPct: "",
    yPct: "",
    // visibility
    isPrivate: true,
    // meta
    difficultyToFind: "Medium",
    accessReq: "",
    lootHint: "",
  });

  // Load doc when opened
  useEffect(() => {
    if (!open || !locationId) return;
    setLoading(true);
    const refDoc = doc(db, "locations", locationId);
    const unsub = onSnapshot(refDoc, (snap) => {
      if (!snap.exists()) {
        setDocData(null);
        setLoading(false);
        return;
      }
      const d = { id: snap.id, ...snap.data() };
      setDocData(d);
      setForm({
        gameKey: d.gameKey || "gta-6",
        game: d.game || labelByKey(d.gameKey || "gta-6"),
        title: d.title || "",
        category: d.category || "Hidden Location",
        city: d.city || "",
        region: d.region || "",
        description: d.description || "",
        tagsCsv: Array.isArray(d.tags) ? d.tags.join(", ") : "",
        coverFile: null,
        coverUrl: d.coverUrl || "",
        images: Array.isArray(d.images) && d.images.length ? d.images : [""],
        shortNote: d.shortNote || "",
        mapImage: d.mapImage || "",
        xPct: d.coordsPct?.xPct ?? "",
        yPct: d.coordsPct?.yPct ?? "",
        isPrivate:
          typeof d.isPrivate === "boolean" ? d.isPrivate : !!d.isHidden, // back-compat
        difficultyToFind: d.difficultyToFind || "Medium",
        accessReq: d.accessReq || "",
        lootHint: d.lootHint || "",
      });
      setLoading(false);
    });
    return () => unsub();
  }, [open, locationId]);

  const updateField = useCallback(
    (k, v) => setForm((s) => ({ ...s, [k]: v })),
    []
  );

  const onChange = (e) => {
    const { name, value, files, type, checked } = e.target;

    if (name === "coverFile") {
      const file = files?.[0] || null;
      if (file) {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type))
          return toast.error("Unsupported image type.");
        if (file.size > MAX_IMAGE_BYTES)
          return toast.error("Image too large. Max 6 MB.");
      }
      setForm((f) => ({ ...f, coverFile: file }));
      return;
    }

    if (name === "gameKey") {
      setForm((f) => ({ ...f, gameKey: value, game: labelByKey(value) }));
      return;
    }

    if (type === "checkbox") {
      setForm((f) => ({ ...f, [name]: checked }));
      return;
    }

    if (name === "xPct" || name === "yPct") {
      setForm((f) => ({ ...f, [name]: value }));
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));
  };

  const updateImageRow = (idx, v) =>
    setForm((s) => {
      const next = [...s.images];
      next[idx] = v;
      return { ...s, images: next };
    });

  const addImageRow = () =>
    setForm((s) => ({ ...s, images: [...s.images, ""] }));

  const removeImageRow = (idx) =>
    setForm((s) => {
      const next = s.images.filter((_, i) => i !== idx);
      return { ...s, images: next.length ? next : [""] };
    });

  // Validation
  const issuesComputed = useMemo(() => {
    const errs = [];
    if (!isAuthed) errs.push("You must be signed in.");
    if (!form.title.trim()) errs.push("Title is required.");

    const urlLike = (v) => {
      if (!v?.trim()) return true;
      try {
        new URL(v.trim());
        return true;
      } catch {
        return false;
      }
    };

    if (form.coverUrl && !urlLike(form.coverUrl))
      errs.push("Cover Image URL is invalid.");
    if (form.mapImage && !urlLike(form.mapImage))
      errs.push("Map Image URL is invalid.");

    form.images.forEach((u, i) => {
      if (u.trim() && !urlLike(u)) errs.push(`Gallery URL #${i + 1} is invalid.`);
    });

    const hasX = String(form.xPct).trim() !== "";
    const hasY = String(form.yPct).trim() !== "";
    if (hasX ^ hasY) errs.push("Provide both X% and Y% for the map pin.");

    const nX = Number(form.xPct),
      nY = Number(form.yPct);
    if (hasX && (Number.isNaN(nX) || nX < 0 || nX > 100))
      errs.push("Map X% must be 0–100.");
    if (hasY && (Number.isNaN(nY) || nY < 0 || nY > 100))
      errs.push("Map Y% must be 0–100.");

    if (form.coverFile) {
      if (!ALLOWED_IMAGE_TYPES.includes(form.coverFile.type))
        errs.push("Cover upload must be JPG/PNG/WebP/GIF.");
      if (form.coverFile.size > MAX_IMAGE_BYTES)
        errs.push("Cover upload exceeds 6 MB.");
    }

    return errs;
  }, [form, isAuthed]);

  useEffect(() => setIssues(issuesComputed), [issuesComputed]);

  const canSubmit = issuesComputed.length === 0 && !submitting && !!docData;

  // Upload (optional)
  const uploadCoverIfAny = async () => {
    if (!form.coverFile || !user?.uid)
      return form.coverUrl?.trim() || (docData?.coverUrl ?? "");
    const file = form.coverFile;
    const safeName = file.name.replace(/[^\w.\-]+/g, "_");
    const path = `locations/${user.uid}/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  // Submit (update)
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const canEdit =
      isAdmin ||
      docData?.createdBy === user.uid ||
      (Array.isArray(docData?.allowEditBy) &&
        docData.allowEditBy.includes(user.uid));

    if (!canEdit) {
      toast.error("You don't have permission to edit this location.");
      return;
    }

    setSubmitting(true);
    try {
      const coverUrl = await uploadCoverIfAny();
      const tags = form.tagsCsv
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      const gallery = Array.from(
        new Set(form.images.map((u) => u.trim()).filter(Boolean))
      );

      const hasX = String(form.xPct).trim() !== "";
      const hasY = String(form.yPct).trim() !== "";
      const coordsPct =
        hasX && hasY
          ? { xPct: parseFloat(form.xPct), yPct: parseFloat(form.yPct) }
          : null;

      const payload = {
        gameKey: form.gameKey,
        game: form.game,
        title: form.title.trim(),
        category: form.category,
        city: form.city.trim() || null,
        region: form.region.trim() || null,
        description: form.description.trim(),
        tags,
        coverUrl: coverUrl || null,
        images: gallery,
        shortNote: form.shortNote.trim() || null,
        mapImage: form.mapImage.trim() || null,
        coordsPct,
        // 👇 visibility (new single field)
        isPrivate: !!form.isPrivate,
        difficultyToFind: form.difficultyToFind || "Medium",
        accessReq: form.accessReq.trim() || null,
        lootHint: form.lootHint.trim() || null,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, "locations", locationId), payload);
      toast.success("Location updated.");
      onClose?.();
    } catch (err) {
      console.error("[locations:updateDoc] error:", err);
      toast.error("Update failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const previewTags = useMemo(
    () => form.tagsCsv.split(",").map((t) => t.trim()).filter(Boolean),
    [form.tagsCsv]
  );

  const mapPreviewSrc = form.mapImage?.trim() || DEFAULT_GAME_MAP_IMG;
  const showPin =
    String(form.xPct).trim() !== "" && String(form.yPct).trim() !== "";
  const mapImgId = useId();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Location"
      size="lg"
      initialFocusRef={titleInputRef}
    >
      {loading ? (
        <div className="mt6-skel">Loading…</div>
      ) : !docData ? (
        <div className="mt6-empty">This location no longer exists.</div>
      ) : (
        <form onSubmit={onSubmit} className="mt6-form">
          {/* Issues */}
          {issues.length > 0 && (
            <ul className="mt6-issues" role="alert" aria-live="assertive">
              {issues.map((m, i) => (
                <li key={i}>• {m}</li>
              ))}
            </ul>
          )}

          {/* Basics */}
          <div className="mt6-section">
            <h4 className="mt6-h4">Basics</h4>
            <div className="mt6-row-3">
              <div>
                <label className="mt6-label" htmlFor="ef-title">
                  Title*
                </label>
                <input
                  id="ef-title"
                  className="mt6-input"
                  name="title"
                  value={form.title}
                  onChange={onChange}
                  required
                  disabled={submitting}
                  ref={titleInputRef}
                  data-autofocus
                />
              </div>

              <div>
                <label className="mt6-label" htmlFor="ef-game">
                  Game*
                </label>
                <select
                  id="ef-game"
                  className="mt6-input"
                  name="gameKey"
                  value={form.gameKey}
                  onChange={onChange}
                  required
                  disabled={submitting}
                >
                  {GAMES.filter((g) => g.key !== "all").map(
                    ({ key, label }) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    )
                  )}
                </select>
              </div>

              {/* Visibility (Public / Private) */}
              <div>
                <label className="mt6-label">Visibility</label>
                <div
                  className="mt6-segment"
                  role="tablist"
                  aria-label="Visibility"
                >
                  <button
                    type="button"
                    className={`mt6-seg-btn ${
                      !form.isPrivate ? "is-active" : ""
                    }`}
                    onClick={() => updateField("isPrivate", false)}
                    disabled={submitting}
                    aria-pressed={!form.isPrivate}
                  >
                    Public
                  </button>
                  <button
                    type="button"
                    className={`mt6-seg-btn ${
                      form.isPrivate ? "is-active" : ""
                    }`}
                    onClick={() => updateField("isPrivate", true)}
                    disabled={submitting}
                    aria-pressed={form.isPrivate}
                  >
                    Private
                  </button>
                </div>
                <div
                  className="mt6-muted"
                  aria-live="polite"
                  style={{ marginTop: 6 }}
                >
                  {form.isPrivate
                    ? "Private: visible only to you."
                    : "Public: visible to everyone."}
                </div>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="mt6-section">
            <h4 className="mt6-h4">Details</h4>
            <div className="mt6-row-3">
              <div>
                <label className="mt6-label" htmlFor="ef-city">
                  City
                </label>
                <input
                  id="ef-city"
                  className="mt6-input"
                  name="city"
                  value={form.city}
                  onChange={onChange}
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="mt6-label" htmlFor="ef-region">
                  Region
                </label>
                <input
                  id="ef-region"
                  className="mt6-input"
                  name="region"
                  value={form.region}
                  onChange={onChange}
                  disabled={submitting}
                />
              </div>
              {/* (Removed checkbox — replaced by toggle above) */}
            </div>

            <div className="mt6-row-3">
              <div>
                <label className="mt6-label" htmlFor="ef-diff">
                  Difficulty to find
                </label>
                <select
                  id="ef-diff"
                  className="mt6-input"
                  name="difficultyToFind"
                  value={form.difficultyToFind}
                  onChange={onChange}
                  disabled={submitting}
                >
                  {["Easy", "Medium", "Hard"].map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mt6-label" htmlFor="ef-access">
                  Access Requirements
                </label>
                <input
                  id="ef-access"
                  className="mt6-input"
                  name="accessReq"
                  value={form.accessReq}
                  onChange={onChange}
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="mt6-label" htmlFor="ef-loot">
                  Loot / Reward Hint
                </label>
                <input
                  id="ef-loot"
                  className="mt6-input"
                  name="lootHint"
                  value={form.lootHint}
                  onChange={onChange}
                  disabled={submitting}
                />
              </div>
            </div>

            <label className="mt6-label" htmlFor="ef-note">
              Short Note (tooltip)
            </label>
            <input
              id="ef-note"
              className="mt6-input"
              name="shortNote"
              value={form.shortNote}
              onChange={onChange}
              disabled={submitting}
            />
          </div>

          {/* Content */}
          <div className="mt6-section">
            <h4 className="mt6-h4">Content</h4>
            <div className="mt6-segment" role="tablist" aria-label="Content mode">
              <button
                type="button"
                className={`mt6-seg-btn ${!previewDesc ? "is-active" : ""}`}
                onClick={() => setPreviewDesc(false)}
                disabled={submitting}
                aria-pressed={!previewDesc}
              >
                Write
              </button>
              <button
                type="button"
                className={`mt6-seg-btn ${previewDesc ? "is-active" : ""}`}
                onClick={() => setPreviewDesc(true)}
                disabled={submitting}
                aria-pressed={previewDesc}
              >
                Preview
              </button>
            </div>

            {!previewDesc ? (
              <textarea
                className="mt6-input mt6-input--textarea"
                name="description"
                value={form.description}
                onChange={onChange}
                rows={6}
                disabled={submitting}
              />
            ) : (
              <div className="mt6-preview">
                {form.description.trim() ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeSanitize]}
                  >
                    {form.description}
                  </ReactMarkdown>
                ) : (
                  <span className="mt6-muted">Nothing to preview.</span>
                )}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="mt6-section">
            <h4 className="mt6-h4">Metadata</h4>
            <label className="mt6-label" htmlFor="ef-tags">
              Tags (CSV)
            </label>
            <input
              id="ef-tags"
              className="mt6-input"
              name="tagsCsv"
              value={form.tagsCsv}
              onChange={onChange}
              disabled={submitting}
            />
            {previewTags.length > 0 && (
              <div className="mt6-pills" style={{ marginTop: 8 }}>
                {previewTags.map((t) => (
                  <span key={t} className="mt6-pill is-active">
                    #{t}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Media */}
          <div className="mt6-section">
            <h4 className="mt6-h4">Media</h4>
            <div className="mt6-row-3">
              <div>
                <label className="mt6-label" htmlFor="ef-cover-file">
                  Replace Cover (upload)
                </label>
                <input
                  id="ef-cover-file"
                  type="file"
                  name="coverFile"
                  accept={ALLOWED_IMAGE_TYPES.join(",")}
                  onChange={onChange}
                  className="mt6-input"
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="mt6-label" htmlFor="ef-cover-url">
                  Cover Image URL
                </label>
                <input
                  id="ef-cover-url"
                  className="mt6-input"
                  name="coverUrl"
                  value={form.coverUrl}
                  onChange={onChange}
                  placeholder="https://…"
                  disabled={submitting}
                />
              </div>
              <div />
            </div>

            <label className="mt6-label">Gallery URLs</label>
            {form.images.map((u, i) => (
              <div className="mt6-row" key={`eg-${i}`}>
                <input
                  className="mt6-input"
                  value={u}
                  onChange={(e) => updateImageRow(i, e.target.value)}
                  placeholder={`Image URL #${i + 1}`}
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="mt6-remove"
                  onClick={() => removeImageRow(i)}
                  disabled={submitting || form.images.length <= 1}
                  aria-label={`Remove image URL ${i + 1}`}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              className="mt6-add"
              onClick={addImageRow}
              disabled={submitting}
            >
              ＋ Add Image
            </button>
          </div>

          {/* Map Pin */}
          <div className="mt6-section">
            <h4 className="mt6-h4">Game Map Pin</h4>
            <div className="mt6-row-3">
              <div>
                <label className="mt6-label" htmlFor="ef-mapimg">
                  Map Image URL
                </label>
                <input
                  id="ef-mapimg"
                  className="mt6-input"
                  name="mapImage"
                  value={form.mapImage}
                  onChange={onChange}
                  placeholder="https://… (override default GTA map image)"
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="mt6-label" htmlFor="ef-xpct">
                  Map X% (0–100)
                </label>
                <input
                  id="ef-xpct"
                  type="number"
                  className="mt6-input"
                  name="xPct"
                  value={form.xPct}
                  onChange={onChange}
                  min={0}
                  max={100}
                  step="0.1"
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="mt6-label" htmlFor="ef-ypct">
                  Map Y% (0–100)
                </label>
                <input
                  id="ef-ypct"
                  type="number"
                  className="mt6-input"
                  name="yPct"
                  value={form.yPct}
                  onChange={onChange}
                  min={0}
                  max={100}
                  step="0.1"
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="mt6-mapbox">
              <div className="mt6-mapimg-wrap">
                <img
                  id={mapImgId}
                  src={mapPreviewSrc}
                  alt="Map preview"
                  className="mt6-mapimg"
                  onClick={(e) => {
                    if (submitting) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = ((e.clientX - rect.left) / rect.width) * 100;
                    const y = ((e.clientY - rect.top) / rect.height) * 100;
                    updateField("xPct", x.toFixed(2));
                    updateField("yPct", y.toFixed(2));
                  }}
                  title="Click to place pin"
                />
                {showPin && (
                  <div
                    className="mt6-mappin"
                    style={{
                      left: `${Number(form.xPct)}%`,
                      top: `${Number(form.yPct)}%`,
                    }}
                    title={form.shortNote || form.title || "Location"}
                  >
                    <span className="mt6-mappin-dot" />
                    <span className="mt6-mappin-pulse" />
                  </div>
                )}
              </div>
              <div className="mt6-maplegend">
                <code>
                  X {String(form.xPct || "—")}%, Y {String(form.yPct || "—")}%
                </code>
                {form.shortNote ? (
                  <span className="mt6-legend-note"> • {form.shortNote}</span>
                ) : null}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt6-actions">
            <button
              type="button"
              className="mt6-btn mt6-btn--ghost"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              className="mt6-btn mt6-btn--primary"
              type="submit"
              disabled={!canSubmit}
              aria-busy={submitting}
            >
              {submitting ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
